import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';

const SimpleEmergencyMap = memo(({ emergencies, selectedMarker, handleMapMarkerClick, setSelectedMarker, getSeverityText, emergencyIdMap }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const infoWindowRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);

  // Initialize map only once
  const initializeMap = useCallback(() => {
    if (!mapRef.current || mapInitialized || !window.google) return;

    const defaultCenter = { lat: 9.005401, lng: 38.763611 };
    
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: defaultCenter,
      zoom: 12,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true
    });

    // Create a single info window to reuse
    infoWindowRef.current = new window.google.maps.InfoWindow();
    
    setMapInitialized(true);
    setMapLoaded(true);
  }, [mapInitialized]);

  // Add or update markers
  const updateMarkers = useCallback(() => {
    if (!mapInstanceRef.current || !emergencies) return;

    // Remove markers that no longer exist
    Object.keys(markersRef.current).forEach(key => {
      if (!emergencies.find(e => e.groupId === key)) {
        markersRef.current[key].setMap(null);
        delete markersRef.current[key];
      }
    });

    const bounds = new window.google.maps.LatLngBounds();
    let hasValidBounds = false;

    emergencies.forEach((emergency, idx) => {
      // Skip if no valid pickup location
      if (!emergency.pickUpLocation?.latitude || !emergency.pickUpLocation?.longitude) return;

      const position = {
        lat: emergency.pickUpLocation.latitude,
        lng: emergency.pickUpLocation.longitude
      };

      // Use readable ID
      const readableId = emergencyIdMap && emergencyIdMap[emergency.groupId] ? emergencyIdMap[emergency.groupId] : emergency.groupId;

      // Custom SVG icon for emergency
      const customIcon = {
        url: 'data:image/svg+xml;utf8,<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="16" cy="16" r="14" fill="%23DC2626" stroke="white" stroke-width="3"/><path d="M16 9v6m0 0v6m0-6h6m-6 0H10" stroke="white" stroke-width="2.5" stroke-linecap="round"/></svg>',
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, 20)
      };

      // Create or update marker
      if (!markersRef.current[emergency.groupId]) {
        const marker = new window.google.maps.Marker({
          position,
          map: mapInstanceRef.current,
          title: `Emergency ${readableId}`,
          icon: customIcon
        });

        // Add click listener
        marker.addListener('click', () => {
          let crewHtml = '';
          if (emergency.crewMembers && emergency.crewMembers.length > 0) {
            crewHtml = `<div style="margin-top:10px;"><strong>Crew Members:</strong><ul style='padding-left:18px;margin:4px 0;'>` +
              emergency.crewMembers.map(crew => `<li>${crew.driverName} (${crew.vehicleType})</li>`).join('') +
              '</ul></div>';
          }
          const content = `
            <div style="padding: 10px; min-width: 220px; font-family: Roboto, Arial, sans-serif;">
              <div style="display:flex;align-items:center;margin-bottom:8px;">
                <span style='display:inline-block;width:28px;height:28px;margin-right:8px;vertical-align:middle;'><svg width='28' height='28' viewBox='0 0 32 32'><circle cx='16' cy='16' r='14' fill='%23DC2626' stroke='white' stroke-width='3'/><path d='M16 9v6m0 0v6m0-6h6m-6 0H10' stroke='white' stroke-width='2.5' stroke-linecap='round'/></svg></span>
                <h4 style="margin: 0; font-size: 1.1rem; font-weight: bold; color: #DC2626;">Emergency ${readableId}</h4>
              </div>
              <p style="margin: 5px 0;"><strong>Patient:</strong> ${emergency.passenger?.name || 'Unknown'}</p>
              <p style="margin: 5px 0;"><strong>Status:</strong> ${emergency.status}</p>
              <p style="margin: 5px 0;"><strong>Severity:</strong> ${getSeverityText(emergency.severity)}</p>
              <p style="margin: 5px 0;"><strong>Distance:</strong> ${emergency.distance || 'N/A'} km</p>
              ${crewHtml}
            </div>
          `;
          infoWindowRef.current.setContent(content);
          infoWindowRef.current.open(mapInstanceRef.current, marker);
          if (handleMapMarkerClick) {
            handleMapMarkerClick(emergency);
          }
        });
        markersRef.current[emergency.groupId] = marker;
      } else {
        // Update existing marker position if needed
        markersRef.current[emergency.groupId].setPosition(position);
      }
      bounds.extend(position);
      hasValidBounds = true;
      // Add destination marker if exists
      if (emergency.destinationLocation?.latitude && emergency.destinationLocation?.longitude) {
        const destPosition = {
          lat: emergency.destinationLocation.latitude,
          lng: emergency.destinationLocation.longitude
        };
        const destMarkerId = `${emergency.groupId}_dest`;
        if (!markersRef.current[destMarkerId]) {
          const destMarker = new window.google.maps.Marker({
            position: destPosition,
            map: mapInstanceRef.current,
            title: `Destination - ${emergency.destinationLocation.address || 'Hospital'}`,
            icon: {
              path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
              fillColor: '#059669',
              fillOpacity: 0.8,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
              scale: 6
            }
          });
          markersRef.current[destMarkerId] = destMarker;
        }
        bounds.extend(destPosition);
      }
    });

    // Fit map to show all markers
    if (hasValidBounds && emergencies.length > 0) {
      mapInstanceRef.current.fitBounds(bounds, { padding: 50 });
    }
  }, [emergencies, handleMapMarkerClick, getSeverityText, emergencyIdMap]);

  // Initialize Google Maps
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.GOOGLE_MAP_API_KEY}`;
      script.async = true;
      script.onload = () => {
        initializeMap();
      };
      document.head.appendChild(script);
    } else {
      initializeMap();
    }

    return () => {
      // Cleanup markers on unmount
      Object.values(markersRef.current).forEach(marker => {
        if (marker && marker.setMap) {
          marker.setMap(null);
        }
      });
      markersRef.current = {};
    };
  }, [initializeMap]);

  // Update markers when emergencies change
  useEffect(() => {
    if (mapLoaded && mapInstanceRef.current) {
      updateMarkers();
    }
  }, [emergencies, mapLoaded, updateMarkers]);

  // Show fallback if no API key
  if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY && !process.env.GOOGLE_MAP_API_KEY) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', height: 500, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Typography variant="h6" gutterBottom>
          Map View Not Available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Google Maps API key is not configured
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: 500 }}>
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
      
      {!mapLoaded && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255,255,255,0.8)'
        }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}, (prevProps, nextProps) => {
  // Only re-render if emergencies actually changed
  return JSON.stringify(prevProps.emergencies) === JSON.stringify(nextProps.emergencies) &&
         prevProps.selectedMarker === nextProps.selectedMarker;
});

export default SimpleEmergencyMap;
